import { DateTime } from 'luxon';
import { CustomerSubscriptionData_CustomerFragment } from '~components/customers/__generated__/CustomersGridItem.graphql';
import { SubscriptionTagVariant } from '~components/ui/SubscriptionTagV2';
import { WaveSubscription, WaveSubscriptionStatus } from '~graphql/__generated__/types';
import { DeepPartial, isDefined } from './types';

export enum WaveSubscriptionEnum {
  Essential = 'Essential',
  Lite = 'Lite',
}

/** Number of days before expiry to show the warning state */
export const EXPIRY_WARNING_DAYS = 15;

/**
 * Checks if a subscription is a valid Wave subscription.
 * @param {WaveSubscriptionDocument | null} subscription - The subscription to check.
 * @returns {boolean} - Returns true if the subscription is defined
 */
export function isWaveSubscription(
  subscription: DeepPartial<WaveSubscription> | null | undefined,
): boolean {
  return isDefined(subscription);
}

/**
 * Determines the subscription type based on the provided subscription object.
 *
 * @param {DeepPartial<WaveSubscription> | null | undefined} subscription - The subscription object to check.
 * @returns {WaveSubscriptionEnum} - Returns `WaveSubscriptionEnum.Essential` if the subscription is a WaveSubscription, otherwise returns `WaveSubscriptionEnum.Lite`.
 */
export function getSubscriptionType(
  subscription: DeepPartial<WaveSubscription> | null | undefined,
): WaveSubscriptionEnum {
  return isWaveSubscription(subscription)
    ? WaveSubscriptionEnum.Essential
    : WaveSubscriptionEnum.Lite;
}

interface CustomerSubscription {
  validUntil: string;
  status: WaveSubscriptionStatus;
}

/**
 * Resolves the cumulative subscription tag variant for a customer based on their subscriptions,
 * display count, and subscription usage.
 *
 * Customer-level rules:
 * - No subscriptions → Lite
 * - waveSubscriptionUsage < displayCount (some displays not covered) → EssentialExpired
 * - Any subscription expiring within 15 days → EssentialWarning
 * - Otherwise → Essential
 *
 * @param subscriptions - The customer's waveSubscriptions array (for expiry checking)
 * @param displayCount - The customer's total display count
 * @param waveSubscriptionUsage - Number of displays that have a subscriptionId set (covered displays)
 */
export function getCustomerSubscriptionTagVariant(
  subscriptions: CustomerSubscription[] | null | undefined,
  displayCount: number,
  waveSubscriptionUsage: number | null | undefined,
): SubscriptionTagVariant {
  if (!subscriptions || subscriptions.length === 0) {
    return SubscriptionTagVariant.Lite;
  }

  const coveredDisplays = waveSubscriptionUsage ?? 0;

  // If some displays are not covered by any subscription, show expired
  if (coveredDisplays < displayCount) {
    return SubscriptionTagVariant.EssentialInsufficient;
  }

  // Check if any subscription is expiring within 15 days
  const now = DateTime.now();
  for (const subscription of subscriptions) {
    const expiryDate = DateTime.fromISO(subscription.validUntil);
    const daysLeft = expiryDate.diff(now, 'days').days;
    if (daysLeft <= EXPIRY_WARNING_DAYS && subscription.status === WaveSubscriptionStatus.Active) {
      return SubscriptionTagVariant.EssentialWarning;
    }
  }

  return SubscriptionTagVariant.Essential;
}
type CustomerSubscriptionData = Omit<CustomerSubscriptionData_CustomerFragment, '__typename'>;

export function useSubscriptionType(data?: CustomerSubscriptionData | null) {
  if (!data)
    return {
      hasWaveSubscription: isWaveSubscription(data),
      hasLiteSubscription: true,
    };
  const customerTag = getCustomerSubscriptionTagVariant(
    data.waveSubscriptions,
    data.displayCount ?? 0,
    data.waveSubscriptionUsage,
  );
  const hasWaveSubscription =
    customerTag === SubscriptionTagVariant.Essential ||
    customerTag == SubscriptionTagVariant.EssentialWarning;

  return {
    hasWaveSubscription,
    hasLiteSubscription: !hasWaveSubscription,
  };
}
